import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getprofile, signoutUser } from "../../Reducers/auth/actions";
import { fetchprofile } from "../../Pages/profile/Reducers/actions";
import { message } from "../../Reducers/loading/actions";
import { Search } from "../../Pages/Items/Reducers/items/actions";
import { fetchbedrijven } from "../../Pages/Admin/Bedrijven/Reducers/actions";
import SearchDropdown from "../../Components/Main/SearchDropdown";
import ThemeToggle from "../../Components/Theme/ThemeToggle";

const Header = ({ username, profile, items, getprofile, signoutUser, message, fetchprofile, fetchbedrijven, Search }) => {
  const dropdownRef = useRef(null);
  const [dropdownMenu, setDropdownMenu] = useState(false);

  // 🔧 Fetch profile & bedrijven on mount
  useEffect(() => {
    const fetchData = async () => {
      const data = await getprofile();
      if (data?.type === "auth_error") signoutUser();
      fetchprofile();
      fetchbedrijven();
    };
    fetchData();
  }, [getprofile, signoutUser, fetchprofile, fetchbedrijven]);

  // 🔧 Update document title
  useEffect(() => {
    document.title = username?.bedrijf ? `Dashboard van ${username.bedrijf.naam}` : "Dashboard";
  }, [username]);

  // 🔧 Click outside & sidebar overlay handlers
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownMenu(false);
    };

    const handleSidebarOverlayClick = (e) => {
      const body = document.body;
      const sidebar = document.querySelector(".sneat-sidebar");

      // Sluit alleen als er geklikt wordt buiten de sidebar en buiten de toggle button
      const toggleButton = document.querySelector(".btn-icon.d-lg-none.me-3");

      const clickInsideSidebar = sidebar && sidebar.contains(e.target);
      const clickOnToggleButton = toggleButton && toggleButton.contains(e.target);

      if (body?.classList.contains("sidebar-open") && !clickInsideSidebar && !clickOnToggleButton && window.innerWidth <= 991.98) {
        body.classList.remove("sidebar-open");
        sidebar.classList.remove("show");
      }
    };

    const handleSidebarLinkClick = (e) => {
      const sidebar = document.querySelector(".sneat-sidebar");
      if (sidebar?.contains(e.target) && window.innerWidth <= 991.98) {
        // Alleen sluiten als het een echte link is (a element), niet bij hoofdmenu items
        const link = e.target.closest("a");
        if (link && link.tagName === "A") {
          document.body.classList.remove("sidebar-open");
          sidebar.classList.remove("show");
        }
      }
    };

    window.addEventListener("click", handleClickOutside);
    window.addEventListener("click", handleSidebarOverlayClick);
    window.addEventListener("click", handleSidebarLinkClick);
    return () => {
      window.removeEventListener("click", handleClickOutside);
      window.removeEventListener("click", handleSidebarOverlayClick);
      window.removeEventListener("click", handleSidebarLinkClick);
    };
  }, []);

  // 🔧 Handlers
  const toggleDropdown = () => setDropdownMenu((prev) => !prev);

  const logout = () => {
    signoutUser();
    message(3000, "info", "Je bent succesvol uitgelogd");
  };

  const openSidebar = () => {
    const sidebar = document.querySelector(".sneat-sidebar");
    const body = document.body;

    if (!sidebar || !body) {
      console.warn("Sidebar or body not found in DOM");
      return;
    }

    if (window.innerWidth <= 991.98) {
      sidebar.classList.toggle("show");
      body.classList.toggle("sidebar-open");
    } else {
      sidebar.classList.toggle("collapsed");
    }
  };
  // 🔧 Profile image component
  const ProfileImage = ({ size = "normal" }) => {
    const className = size === "large" ? "profile-img-large" : "profile-img";
    const src = profile?.portimage || "/img/nofoto.png";
    return <img src={src} title="Logo Recraplan" className={className} alt="Gebruiker foto" />;
  };

  return (
    <header className="sneat-header mx-4 mb-2">
      {/* Sidebar toggle */}
      <button 
        className="btn btn-icon d-lg-none me-3 p-2 rounded-pill" 
        onClick={openSidebar}
        style={{ 
          backgroundColor: 'var(--bs-gray-100)', 
          border: '1px solid var(--bs-gray-200)',
          transition: 'all 0.2s ease-in-out'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--bs-gray-200)';
          e.target.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'var(--bs-gray-100)';
          e.target.style.transform = 'scale(1)';
        }}
      >
        <i className="fas fa-bars" style={{ fontSize: '1rem', color: 'var(--bs-gray-700)' }}></i>
      </button>

      {/* Search */}
      {username?.bedrijf?.modules.includes("items") && (
        <div className="sneat-search d-none d-md-block">
          <SearchDropdown items={items} Search={Search} />
        </div>
      )}

      <h5 className="box-title">Welkom bij {username?.bedrijf?.naam || ""}</h5>

      <div className="sneat-header-actions">
        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Notifications */}
        <button className="btn-icon d-none d-md-block">
          <i className="fas fa-bell"></i>
        </button>

        {/* User Dropdown */}
        <div className="sneat-user-dropdown dropdown" ref={dropdownRef}>
          <button className="dropdown-toggle" onClick={toggleDropdown} aria-expanded={dropdownMenu}>
            <ProfileImage />
            <div className="d-none d-sm-block ms-2">
              <div className="fw-medium">
                {username?.voornaam} {username?.achternaam}
              </div>
              <small className="text-muted">{username?.role}</small>
            </div>
            <i className="fas fa-chevron-down ms-2 d-none d-sm-block"></i>
          </button>

          <ul className={`dropdown-menu dropdown-menu-end ${dropdownMenu ? "show" : ""}`}>
            <li>
              <div className="dropdown-item-text">
                <div className="d-flex align-items-center">
                  <ProfileImage size="large" />
                  <div className="ms-3">
                    <div className="fw-medium">
                      {username?.voornaam} {username?.achternaam}
                    </div>
                    <small className="text-muted">{username?.bedrijf?.naam}</small>
                  </div>
                </div>
              </div>
            </li>
            <li>
              <hr className="dropdown-divider" />
            </li>

            {username?.role !== "medewerker" && (
              <li>
                <Link to="/admin/profile" className="dropdown-item">
                  <i className="fas fa-user me-2"></i> Profiel
                </Link>
              </li>
            )}
            <li>
              <hr className="dropdown-divider" />
            </li>
            <li>
              <Link to="/login" onClick={logout} className="dropdown-item">
                <i className="fas fa-sign-out-alt me-2"></i> Uitloggen
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

const mapStateToProps = (state) => ({
  errors: state.auth.error,
  username: state.auth.user,
  profile: state.profile,
  items: state.items.items,
});

export default connect(mapStateToProps, { fetchprofile, getprofile, signoutUser, message, fetchbedrijven, Search })(Header);
